import { useCallback, useContext, useMemo, useState } from 'react';
import { SET_PROPERTY_FILTERS } from 'reducers/reducer';
import {
  FILTER_TYPE,
  RADIO_OPTIONS_FLAGS
} from 'screens/Property/PropertiesLanding/components/PropertyTreeView/components/PropertyFilters/constants';
import _ from 'lodash';
import createAction from 'helpers/createAction';
import {
  filterPropertyTree,
  hasCropZoneFilters
} from 'screens/Property/PropertiesLanding/helpers/propertyFilters';
import { Context } from '../components/Store';

const usePropertyFilters = (type, isHugeOrganization = false) => {
  const [
    { propertyFilters, tagCatalogue, organization },
    dispatch
  ] = useContext(Context);
  const activeTagCatalogue =
    type !== FILTER_TYPE.reports &&
    tagCatalogue &&
    tagCatalogue.organizationId === organization?.id
      ? tagCatalogue.tags
      : undefined;
  const {
    showArchivedProperties,
    showFarmWithIds,
    tasksFiltersConfig,
    cropZoneFilters
  } = propertyFilters || {};
  const [properties, setProperties] = useState([]);
  const [tasksTags, setTasksTags] = useState([]);
  const { showTaskTagsWithIds } = tasksFiltersConfig || {};

  const filterOutArchivedProperties = propertyList => {
    return (
      propertyList
        ?.filter(property => !property.archived)
        .map(property => ({
          ...property,
          fields: property.fields?.filter(field => !field.archived) || []
        })) || []
    );
  };

  const sortAndIncludeArchivedProperties = useCallback(
    propertyList => {
      if (!propertyList) return [];
      const sort = (arr, sortFn) =>
        isHugeOrganization ? arr : arr.sort(sortFn);
      return sort(
        propertyList,
        (propertyA, propertyB) => propertyA.archived - propertyB.archived
      ).map(property =>
        property.fields
          ? {
              ...property,
              fields: property.fields.sort(
                (fieldA, fieldB) => fieldA.archived - fieldB.archived
              )
            }
          : property
      );
    },
    [isHugeOrganization]
  );

  const propertiesFilter = useMemo(() => {
    let propertyList;
    if (showArchivedProperties) {
      propertyList = sortAndIncludeArchivedProperties(properties);
    } else {
      propertyList = filterOutArchivedProperties(properties);
    }

    if (showFarmWithIds?.length > 0) {
      propertyList = propertyList.filter(property =>
        showFarmWithIds?.includes(property.id)
      );
    }
    return filterPropertyTree(
      propertyList,
      {
        showArchivedProperties,
        cropZoneFilters
      },
      activeTagCatalogue
    );
  }, [
    showArchivedProperties,
    properties,
    showFarmWithIds,
    cropZoneFilters,
    activeTagCatalogue,
    sortAndIncludeArchivedProperties
  ]);

  const taskTagsFilter = useMemo(() => {
    if (!showTaskTagsWithIds || showTaskTagsWithIds.length === 0) {
      return tasksTags;
    }
    if (tasksFiltersConfig.radioOption === RADIO_OPTIONS_FLAGS.anyTags) {
      return tasksTags.filter(task =>
        task.flags?.some(flag =>
          showTaskTagsWithIds.includes(
            typeof flag === 'string' ? flag : flag?.id
          )
        )
      );
    }
    if (tasksFiltersConfig.radioOption === RADIO_OPTIONS_FLAGS.allTags) {
      return tasksTags.filter(task =>
        showTaskTagsWithIds.every(id =>
          task.flags?.some(
            flag => (typeof flag === 'string' ? flag : flag?.id) === id
          )
        )
      );
    }
    return [];
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [tasksTags, tasksFiltersConfig]);

  const setShowArchivedProperties = enable => {
    dispatch({
      type: 'SET_PROPERTY_FILTERS',
      payload: {
        showArchivedProperties: enable
      }
    });
  };

  const setFarmsFilter = ids => {
    dispatch({
      type: 'SET_PROPERTY_FILTERS',
      payload: {
        showFarmWithIds: ids
      }
    });
  };

  const setTaskTagsFilter = newTasksFiltersConfig => {
    createAction(dispatch, SET_PROPERTY_FILTERS, {
      tasksFiltersConfig: {
        radioOption: newTasksFiltersConfig.radioOption,
        showTaskTagsWithIds: newTasksFiltersConfig.showTaskTagsWithIds
      }
    });
  };

  const runMapFilter = useCallback(
    list => {
      if (showArchivedProperties) {
        return list;
      }

      return list.features
        ? {
            ...list,
            features: list.features.filter(f => !f.properties?.archived)
          }
        : [];
    },
    [showArchivedProperties]
  );

  const hasActiveFilters = () => {
    if (type === FILTER_TYPE.reports) {
      return showTaskTagsWithIds.length > 0;
    }
    return (
      showArchivedProperties ||
      hasCropZoneFilters(cropZoneFilters) ||
      showFarmWithIds?.length > 0
    );
  };

  const hasFilterChanges = ({
    selectedFarmIds,
    showLocalArchivedProperties,
    localTasksFiltersConfig,
    localCropZoneFilters
  }) =>
    !_.isEqual(_.sortBy(selectedFarmIds), _.sortBy(showFarmWithIds)) ||
    showLocalArchivedProperties !== showArchivedProperties ||
    !_.isEqual(localTasksFiltersConfig, tasksFiltersConfig) ||
    (localCropZoneFilters !== undefined &&
      !_.isEqual(localCropZoneFilters, cropZoneFilters));

  const applyFilters = ({
    selectedFarmIds,
    showLocalArchivedProperties,
    newConfigTaskTagsFilter,
    newCropZoneFilters
  }) => {
    if (type !== FILTER_TYPE.reports) {
      dispatch({
        type: SET_PROPERTY_FILTERS,
        payload: {
          showFarmWithIds: selectedFarmIds,
          showArchivedProperties: showLocalArchivedProperties,
          ...(newCropZoneFilters ? { cropZoneFilters: newCropZoneFilters } : {})
        }
      });
      return;
    }
    setFarmsFilter(selectedFarmIds);
    setShowArchivedProperties(showLocalArchivedProperties);
    setTaskTagsFilter(newConfigTaskTagsFilter);
  };

  return {
    filteredProperties: propertiesFilter || [],
    filteredTasksByTags: taskTagsFilter,
    showArchivedProperties,
    setShowArchivedProperties,
    setPropertiesToFilter: setProperties,
    setTaskTagsToFilter: setTasksTags,
    hasActiveFilters,
    hasFilterChanges,
    runMapFilter,
    applyFilters
  };
};

export default usePropertyFilters;
